import { useEffect, useState } from "react";
import ResourcePersonNavBar from "../components/ResourcePersonNavBar";
import { useResourcePersonJournals } from "../store/resourcePerson/resourcePersonStore";
import UploadField from "../components/resourcePerson/UploadField";
import ResourceAnalyticsPanel from "../components/resourcePerson/ResourceAnalyticsPanel";
import ResourceDownloadLink from "../components/resourcePerson/ResourceDownloadLink";
import { RichTextField, cleanHtmlForSave } from "../components/resourcePerson/RichTextField";
import { StringListEditor, TableOfContentsEditor } from "../components/resourcePerson/MetadataEditors";
import {useConfirmDialog} from "../components/ConfirmDialogProvider";
import {useNavigate, useParams} from "react-router-dom";
import {
  ACADEMIC_LEVELS,
  FILE_FORMATS,
  arrayToLines,
  linesToArray,
  parseTableOfContents,
  stringifyTableOfContents,
} from "../utils/resourceMetadata";

const EMPTY_FORM = {
  title: "",
  author: "",
  description: "",
  summary: "",
  abstract: "",
  chapterOnePreview: "",
  tableOfContents: "",
  whatIsIncluded: "",
  academicLevel: "",
  fieldOfStudy: "",
  fileFormat: "",
  price: "",
  coverImage: "",
  fileUrl: "",
  filePublicId: "",
  fileCloudType: "",
  fileDeliveryType: "",
};
const STATUSES = ["", "DRAFT", "PENDING", "APPROVED", "REJECTED", "DISABLED", "WITHDRAWN"];
const inputClass =
  "w-full rounded-lg border border-blue-100 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100";

/**
 * Journals page - resource-person journal authoring.
 * POST/GET /api/resource-person/journals, GET/PATCH/DELETE :id,
 * PATCH :id/submit, PATCH :id/unsubmit, GET :id/downloads, GET :id/analytics.
 */
export default function JournalsPage() {
  const {id: routeJournalId} = useParams();
  const navigate = useNavigate();
  const store = useResourcePersonJournals();
  const confirm = useConfirmDialog();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const load = (overrides = {}) => store.fetchJournals({
    page, limit: 10,
    ...(search ? { search } : {}),
    ...(status ? { status } : {}),
    ...overrides,
  });

  useEffect(() => {
    if (routeJournalId) return;
    setPage(1);
    load({page: 1});
  }, [search, status, routeJournalId]);
  useEffect(() => {
    if (routeJournalId) return;
    load();
  }, [page, routeJournalId]);

  useEffect(() => {
    if (!routeJournalId) return;
    store.fetchJournalById(routeJournalId);
    store.fetchJournalDownloads(routeJournalId, { page: 1, limit: 10 });
    store.fetchJournalAnalytics(routeJournalId);
  }, [routeJournalId, store.fetchJournalById, store.fetchJournalDownloads, store.fetchJournalAnalytics]);


  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError("");
    setMessage("");
    setShowForm(true);
  };

  const openEdit = (journal) => {
    setEditingId(journal.id || journal._id);
    setForm({
      title: journal.title || "",
      author: journal.author || "",
      description: journal.description || "",
      summary: journal.summary || "",
      abstract: journal.abstract || "",
      chapterOnePreview: journal.chapterOnePreview || journal.chapter_one_preview || "",
      tableOfContents: stringifyTableOfContents(journal.tableOfContents || journal.table_of_contents),
      whatIsIncluded: arrayToLines(journal.whatIsIncluded || journal.what_is_included),
      academicLevel: journal.academicLevel || journal.academic_level || "",
      fieldOfStudy: journal.fieldOfStudy || journal.field_of_study || "",
      fileFormat: journal.fileFormat || journal.file_format || "",
      price: journal.price ?? "",
      coverImage: journal.coverImage || journal.cover_image || "",
      fileUrl: journal.fileUrl || journal.file_url || "",
      filePublicId: journal.filePublicId || journal.file_public_id || "",
      fileCloudType: journal.fileCloudType || journal.file_cloud_type || "",
      fileDeliveryType: journal.fileDeliveryType || journal.file_delivery_type || "",
    });
    setError("");
    setMessage("");
    setShowForm(true);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setError("");
    if (!form.title.trim() || !form.author.trim() || !form.description.trim() || !form.fileUrl) {
      setError("Title, author, description, and the journal file are required.");
      return;
    }
    let tableOfContents;
    try {
      tableOfContents = parseTableOfContents(form.tableOfContents);
    } catch (err) {
      setError(err.message);
      return;
    }

    const payload = {
      title: form.title,
      author: form.author,
      description: cleanHtmlForSave(form.description),
      ...(cleanHtmlForSave(form.summary) ? { summary: cleanHtmlForSave(form.summary) } : {}),
      ...(cleanHtmlForSave(form.abstract) ? { abstract: cleanHtmlForSave(form.abstract) } : {}),
      ...(cleanHtmlForSave(form.chapterOnePreview)
        ? { chapterOnePreview: cleanHtmlForSave(form.chapterOnePreview) }
        : {}),
      ...(tableOfContents.length ? { tableOfContents } : {}),
      ...(linesToArray(form.whatIsIncluded).length
        ? { whatIsIncluded: linesToArray(form.whatIsIncluded) }
        : {}),
      ...(form.academicLevel ? { academicLevel: form.academicLevel } : {}),
      ...(form.fieldOfStudy.trim() ? { fieldOfStudy: form.fieldOfStudy.trim() } : {}),
      ...(form.fileFormat ? { fileFormat: form.fileFormat } : {}),
      price: form.price === "" ? undefined : Number(form.price),
      ...(form.coverImage ? { coverImage: form.coverImage } : {}),
      ...(form.fileUrl ? { fileUrl: form.fileUrl } : {}),
      ...(form.filePublicId ? { filePublicId: form.filePublicId } : {}),
      ...(form.fileCloudType ? { fileCloudType: form.fileCloudType } : {}),
      ...(form.fileDeliveryType ? { fileDeliveryType: form.fileDeliveryType } : {}),
    };
    const result = editingId ? await store.updateJournal(editingId, payload) : await store.createJournal(payload);
    if (!result.success) { setError(result.error || "Save failed"); return; }
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setMessage(editingId ? "Journal updated" : "Journal draft created");
    load();
  };

  const handleDelete = async (id) => {
    return confirm({
      title: "Delete this draft journal?",
      description: "This action cannot be undone.",
      confirmLabel: "Delete journal",
      onConfirm: async () => {
        const result = await store.deleteJournal(id);
        if (!result.success) throw new Error(result.error || "Delete failed");
        setMessage("Journal deleted");
        load();
      },
    });
  };

  const handleSubmit = async (id) => {
    const result = await store.submitJournal(id);
    setMessage(result.success ? "Submitted for review" : (result.error || "Submit failed"));
    if (result.success) load();
  };

  const handleUnsubmit = async (id) => {
    const result = await store.unsubmitJournal(id);
    setMessage(result.success ? "Withdrawn from review" : (result.error || "Unsubmit failed"));
    if (result.success) load();
  };

  const detail = String(store.journalDetail?.id || store.journalDetail?._id || "") === String(routeJournalId || "")
    ? store.journalDetail
    : null;

  const setField = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));
  const isDraft = (journal) => String(journal?.status || "").toUpperCase() === "DRAFT";
  const isEditable = (journal) =>
    ["DRAFT", "REJECTED"].includes(String(journal?.status || "").toUpperCase());

  return (
    <div className="min-h-screen bg-stone-50 font-sans text-stone-900">
      <ResourcePersonNavBar title="Journals" subTitle="Authoring" />

      <main className="mx-auto max-w-6xl px-5 pb-10 pt-4">
        {routeJournalId ? (
          <header className="relative mb-6 overflow-hidden rounded-2xl bg-gradient-to-br from-blue-950 via-blue-800 to-blue-600 px-6 py-7 text-white shadow-lg sm:px-8">
            <div className="relative z-10 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-200">Resource library</p>
                <h1 className="text-3xl font-bold tracking-tight">{detail?.title || "Journal details"}</h1>
                <p className="mt-2 text-sm text-blue-100">Journal information, reader activity and performance.</p>
              </div>
              <button
                type="button"
                onClick={() => navigate("/resource-person/journals")}
                className="rounded-xl bg-white px-4 py-2.5 text-sm font-bold text-blue-800 shadow-sm transition hover:bg-blue-50"
              >
                Back to journals
              </button>
            </div>
            <div aria-hidden="true" className="absolute -right-8 -top-20 h-64 w-64 rounded-full border-[28px] border-white/10" />
          </header>
        ) : null}
        {!routeJournalId && !showForm && <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-blue-700">My Journals</h1>
            <p className="text-sm text-stone-500">
              Create drafts, submit them for review and track downloaders.
            </p>
          </div>
          <button
            onClick={openCreate}
            className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-800"
          >
            New Journal
          </button>
        </div>}

        {!routeJournalId && !showForm && message && (
          <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">
            {message}
          </div>
        )}
        {!routeJournalId && !showForm && (error || store.journalsError) && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
            {error || store.journalsError}
          </div>
        )}

        {!routeJournalId && !showForm && <div className="mb-4 flex flex-wrap gap-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search journals…"
            className="min-w-[220px] flex-1 rounded-lg border border-blue-100 bg-white px-3 py-2 text-sm outline-none focus:border-blue-400"
          />
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-lg border border-blue-100 bg-white px-3 py-2 text-sm outline-none focus:border-blue-400"
          >
            {STATUSES.map((value) => (
              <option key={value || "ALL"} value={value}>
                {value || "All statuses"}
              </option>
            ))}
          </select>
        </div>}
        {!routeJournalId && showForm && (
          <>
            <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
                  Resource library
                </p>
                <h1 className="text-3xl font-bold tracking-tight text-stone-900">
                  {editingId ? "Edit journal" : "Create a journal"}
                </h1>
                <p className="mt-2 max-w-xl text-sm text-stone-500">
                  Add the journal details and files, then save to return to your collection.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  setForm(EMPTY_FORM);
                  setError("");
                }}
                className="rounded-xl border border-stone-200 bg-white px-4 py-2.5 text-sm font-bold text-stone-700 shadow-sm transition hover:bg-stone-50"
              >
                Back to journals
              </button>
            </header>

            {error && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <section className="rounded-2xl border border-blue-100 bg-white p-5 shadow-sm">
              <h2 className="mb-4 text-lg font-semibold">
                {editingId ? "Edit journal draft" : "New journal draft"}
              </h2>
              <form onSubmit={handleSave} className="grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-1 text-sm font-medium">
                  Title
                  <input
                    required
                    type="text"
                    className={inputClass}
                    value={form.title}
                    onChange={setField("title")}
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm font-medium">
                  Author
                  <input
                    type="text"
                    className={inputClass}
                    value={form.author}
                    onChange={setField("author")}
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm font-medium">
                  Price
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className={inputClass}
                    value={form.price}
                    onChange={setField("price")}
                    placeholder="0 = free"
                  />
                </label>
                <div className="flex flex-col gap-1 text-sm font-medium md:col-span-2">
                  Description
                  <RichTextField
                    value={form.description}
                    minHeight={140}
                    maxHeight={260}
                    placeholder="Describe the journal. Formatting is supported."
                    onChange={(description) => setForm((current) => ({ ...current, description }))}
                  />
                </div>
                <div className="flex flex-col gap-1 text-sm font-medium md:col-span-2">
                  Summary
                  <RichTextField
                    value={form.summary}
                    minHeight={80}
                    maxHeight={160}
                    placeholder="Short listing summary"
                    onChange={(summary) => setForm((current) => ({ ...current, summary }))}
                  />
                </div>
                <div className="flex flex-col gap-1 text-sm font-medium md:col-span-2">
                  Abstract
                  <RichTextField
                    value={form.abstract}
                    minHeight={100}
                    maxHeight={220}
                    onChange={(abstract) => setForm((current) => ({ ...current, abstract }))}
                  />
                </div>
                <div className="flex flex-col gap-1 text-sm font-medium md:col-span-2">
                  Chapter one preview
                  <RichTextField
                    value={form.chapterOnePreview}
                    minHeight={100}
                    maxHeight={220}
                    onChange={(chapterOnePreview) =>
                      setForm((current) => ({ ...current, chapterOnePreview }))
                    }
                  />
                </div>
                <label className="flex flex-col gap-1 text-sm font-medium">
                  Academic level
                  <select
                    className={inputClass}
                    value={form.academicLevel}
                    onChange={setField("academicLevel")}
                  >
                    {ACADEMIC_LEVELS.map((level) => (
                      <option key={level || "none"} value={level}>
                        {level || "Not set"}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-sm font-medium">
                  File format
                  <select
                    className={inputClass}
                    value={form.fileFormat}
                    onChange={setField("fileFormat")}
                  >
                    {FILE_FORMATS.map((format) => (
                      <option key={format || "none"} value={format}>
                        {format || "Not set"}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-sm font-medium md:col-span-2">
                  Field of study
                  <input
                    className={inputClass}
                    value={form.fieldOfStudy}
                    onChange={setField("fieldOfStudy")}
                    placeholder="e.g. Public Health"
                  />
                </label>
                <div className="flex flex-col gap-1 text-sm font-medium md:col-span-2">
                  What is included
                  <StringListEditor
                    value={form.whatIsIncluded}
                    onChange={(whatIsIncluded) =>
                      setForm((current) => ({ ...current, whatIsIncluded }))
                    }
                    placeholder="e.g. Full journal PDF"
                  />
                </div>
                <div className="flex flex-col gap-1 text-sm font-medium md:col-span-2">
                  Table of contents
                  <TableOfContentsEditor
                    value={form.tableOfContents}
                    onChange={(tableOfContents) =>
                      setForm((current) => ({ ...current, tableOfContents }))
                    }
                  />
                </div>
                <UploadField
                  kind="image"
                  label="Cover image"
                  maxSizeBytes={5 * 1024 * 1024}
                  hint="JPG, PNG, WebP or GIF · maximum 5 MB"
                  value={form.coverImage}
                  onChange={(url) => setForm((current) => ({ ...current, coverImage: url }))}
                />
                <UploadField
                  kind="document"
                  label="Journal file"
                  accept=".pdf"
                  value={form.fileUrl}
                  onChange={(url, upload) =>
                    setForm((current) => ({
                      ...current,
                      fileUrl: url,
                      filePublicId: upload?.publicId || upload?.public_id || "",
                      fileCloudType: upload?.cloudType || upload?.cloud_type || "",
                      fileDeliveryType: upload?.deliveryType || upload?.delivery_type || "",
                    }))
                  }
                />
                <div className="flex flex-wrap gap-2 md:col-span-2">
                  <button
                    type="submit"
                    disabled={store.journalMutationLoading}
                    className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-800 disabled:opacity-60"
                  >
                    {store.journalMutationLoading ? "Saving…" : "Save draft"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingId(null);
                      setForm(EMPTY_FORM);
                      setError("");
                    }}
                    className="rounded-lg bg-stone-100 px-4 py-2 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-200"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </section>
          </>
        )}

        {!routeJournalId && !showForm && <>
        <div className="overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-sm">
          {store.journalsLoading ? (
            <div className="flex justify-center py-16">
              <div className="loader" />
            </div>
          ) : store.journals.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-stone-400">
              No journals yet. Create your first draft.
            </p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="bg-blue-50 text-xs uppercase tracking-wide text-blue-700">
                <tr>
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3">Author</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Price</th>
                  <th className="px-4 py-3">Downloads</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {store.journals.map((journal) => (
                  <tr key={journal.id} className="border-t border-blue-50">
                    <td className="px-4 py-3 font-medium text-stone-800">
                      <button
                        onClick={() => navigate(`/resource-person/journals/${journal.id || journal._id}`)}
                        className="text-left hover:text-blue-700 hover:underline"
                      >
                        {journal.title || "Untitled"}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-stone-600">{journal.author || "—"}</td>
                    <td className="px-4 py-3">
                      <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
                        {journal.status || "DRAFT"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-stone-600">
                      {journal.price === null || journal.price === undefined ? "—" : journal.price}
                    </td>
                    <td className="px-4 py-3 text-stone-600">
                      {journal.downloadCount ?? journal.downloads ?? 0}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap justify-end gap-2">
                        <button
                          onClick={() => openEdit(journal)}
                          disabled={!isEditable(journal)}
                          className="rounded-md border border-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-50 disabled:opacity-40"
                        >
                          Edit
                        </button>
                        {isEditable(journal) ? (
                          <button
                            onClick={() => handleSubmit(journal.id)}
                            className="rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-emerald-700"
                          >
                            Submit
                          </button>
                        ) : String(journal.status || "").toUpperCase() === "PENDING" ? (
                          <button
                            onClick={() => handleUnsubmit(journal.id)}
                            className="rounded-md bg-amber-500 px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-amber-600"
                          >
                            Unsubmit
                          </button>
                        ) : null}
                        <button
                          onClick={() => handleDelete(journal.id)}
                          disabled={!isEditable(journal)}
                          className="rounded-md border border-red-200 px-2.5 py-1 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-40"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {store.journalsPages > 1 && (
          <div className="mt-4 flex items-center justify-center gap-3">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="rounded-lg border border-blue-100 bg-white px-3 py-1.5 text-sm font-semibold text-blue-700 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-sm text-stone-500">
              Page {page} of {store.journalsPages} · {store.journalsTotal} total
            </span>
            <button
              onClick={() => setPage((p) => Math.min(store.journalsPages, p + 1))}
              disabled={page >= store.journalsPages}
              className="rounded-lg border border-blue-100 bg-white px-3 py-1.5 text-sm font-semibold text-blue-700 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
        </>}

        {routeJournalId && (
          <section className="mt-8 rounded-2xl border border-blue-100 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-bold text-blue-700">
                {detail?.title || "Journal detail"}
              </h2>
            </div>

            {store.journalDetailLoading || store.journalDownloadsLoading || store.journalAnalyticsLoading ? (
              <div className="flex justify-center py-8">
                <div className="loader" />
              </div>
            ) : store.journalDetailError ? (
              <p className="text-sm text-red-600">{store.journalDetailError}</p>
            ) : detail ? (
              <>
                <dl className="grid gap-3 sm:grid-cols-2">
                  {detail.coverImage || detail.cover_image ? (
                    <div className="sm:col-span-2">
                      <dt className="text-xs font-semibold uppercase text-stone-400">Cover image</dt>
                      <dd className="mb-0 mt-2">
                        <img
                          src={detail.coverImage || detail.cover_image}
                          alt={`${detail.title || "Journal"} cover`}
                          className="h-40 w-28 rounded-lg border border-blue-50 object-cover shadow-sm"
                        />
                      </dd>
                    </div>
                  ) : null}
                  {[
                    ["Author", detail.author],
                    ["Status", detail.status || "DRAFT"],
                    ["Price", detail.price === null || detail.price === undefined ? "—" : new Intl.NumberFormat("en-NG", {style: "currency", currency: "NGN"}).format(detail.price)],
                    ["Downloads", detail.downloads ?? 0],
                    ["Revenue", detail.revenue === null || detail.revenue === undefined ? "—" : new Intl.NumberFormat("en-NG", {style: "currency", currency: "NGN"}).format(detail.revenue)],
                    ["Publisher", detail.publisher?.name],
                    ["Uploaded", detail.uploadedAt],
                    ["Submitted", detail.submittedAt],
                    ["Updated", detail.updatedAt],
                    ["Moderator rejection reason", detail.rejectionReason],
                  ].map(([label, value]) => (
                    <div key={label} className={label === "Moderator rejection reason" ? "sm:col-span-2" : ""}>
                      <dt className="text-xs font-semibold uppercase text-stone-400">{label}</dt>
                      <dd className="break-words text-sm text-stone-700">
                        {value === null || value === undefined || value === "" ? "—" :
                          /uploaded|submitted|updated/i.test(label) && !Number.isNaN(Date.parse(value))
                            ? new Date(value).toLocaleString()
                            : value}
                      </dd>
                    </div>
                  ))}
                  <div className="sm:col-span-2">
                    <dt className="text-xs font-semibold uppercase text-stone-400">Description</dt>
                    <dd className="whitespace-pre-wrap text-sm text-stone-700">{detail.description || "—"}</dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-xs font-semibold uppercase text-stone-400">Downloadable file</dt>
                    <dd className="mt-1"><ResourceDownloadLink url={detail.fileUrl || detail.file_url} /></dd>
                  </div>
                </dl>

                <h3 className="mt-6 mb-2 text-sm font-bold text-blue-700">Analytics</h3>
                <ResourceAnalyticsPanel
                  analytics={store.journalAnalytics}
                  loading={store.journalAnalyticsLoading}
                  error={store.journalAnalyticsError}
                />

                <h3 className="mt-6 mb-2 text-sm font-bold text-blue-700">
                  Downloaders ({store.journalDownloads.length})
                </h3>
                {store.journalDownloadsLoading ? (
                  <p className="text-sm text-stone-400">Loading downloads…</p>
                ) : store.journalDownloadsError ? (
                  <p className="text-sm text-red-600">{store.journalDownloadsError}</p>
                ) : store.journalDownloads.length === 0 ? (
                  <p className="text-sm text-stone-400">No downloads recorded yet.</p>
                ) : (
                  <ul className="divide-y divide-blue-50 rounded-lg border border-blue-50">
                    {store.journalDownloads.map((item, index) => (
                      <li
                        key={item.id || index}
                        className="flex items-center justify-between px-3 py-2 text-sm"
                      >
                        <span className="text-stone-700">
                          {[item.first_name, item.last_name].filter(Boolean).join(" ") ||
                            item.name ||
                            item.user?.email ||
                            item.email ||
                            `Downloader ${index + 1}`}
                        </span>
                        <span className="text-xs text-stone-400">
                          {item.downloaded_at || item.created_at || item.createdAt || ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : null}
          </section>
        )}

      </main>
    </div>
  );
}
